import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Container,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Stack,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import { useApplyTxQueryParams } from '../hooks/useApplyTxQueryParams';

import TransactionTable from '../components/transactions/TransactionTable';
import { Transaction } from '../types/Transaction';
import FloatingActionButton from '../components/common/FloatingActionButton';
import TransactionModal from '../components/transactions/TransactionModal';
import TransactionDetailsDrawer from '../components/transactions/TransactionDetailsDrawer';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import RecurringScopeDialog, { RecurringScope } from '../components/transactions/RecurringScopeDialog';
import { useAuth } from '../context/AuthContext';

import SummaryCard from '../components/dashboard/SummaryCard';
import {
  TransactionFilters,
  TransactionListMode,
} from '../hooks/useTransactions';
import { useLedgerTransactions } from '../hooks/useLedgerTransactions';
import { authFetch } from '../lib/authFetch';

import {
  ListAlt as TransactionsIcon,
} from '@mui/icons-material';
import DifferenceIcon from '@mui/icons-material/Difference';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import AddIcon from '@mui/icons-material/Add';

import { getTodayUTCString, formatDateRange } from '../utils/dateUtils';

const LOCAL_FILTER_KEY = 'transactionsFilters';
const LOCAL_LISTMODE_KEY = `${LOCAL_FILTER_KEY}:listMode`;
const TAG_OPTIONS = ['bill', 'utility', 'paycheck', 'bonus', 'subscription', 'groceries', 'education', 'other'] as const;

const getLocalDateStamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFilenameFromDisposition = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].trim());

  const basicMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (basicMatch?.[1]) return basicMatch[1].trim();

  return null;
};

const TransactionsPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Transactions | Velora Money';
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const { auth } = useAuth();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletingTx, setDeletingTx] = useState<Transaction | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeMode, setScopeMode] = useState<'edit' | 'delete'>('edit');
  const [scopeTx, setScopeTx] = useState<Transaction | null>(null);
  const [seriesScope, setSeriesScope] = useState<Exclude<RecurringScope, 'instance'> | null>(null);
  const [detailsTx, setDetailsTx] = useState<Transaction | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState<TransactionFilters>({
    type: undefined,
    category: '',
    startDate: '',
    endDate: '',
    isRecurring: undefined,
    tags: [],
  });
  const [showFuture, setShowFuture] = useState<boolean>(false);
  const [listMode, setListMode] = useState<TransactionListMode>(() => {
    const raw = localStorage.getItem(LOCAL_LISTMODE_KEY);
    return raw === 'committed' || raw === 'all' || raw === 'tentative' ? raw : 'committed';
  });

  // ✅ Track when we've hydrated from localStorage so URL params can override afterward
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  // Load filters from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_FILTER_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        let normalizedTags: string[] = [];
        if (Array.isArray(parsed.tags)) normalizedTags = parsed.tags;
        else if (typeof parsed.tags === 'string') {
          normalizedTags = parsed.tags.split(',').map((t: string) => t.trim()).filter(Boolean);
        }

        setFilters(prev => ({
          ...prev,
          ...parsed,
          tags: normalizedTags.length ? normalizedTags : prev.tags ?? [],
        }));
      } catch {
        console.warn('Failed to parse stored filters');
      }
    }
    setFiltersHydrated(true); // ✅ now URL params can apply
  }, []);

  // ✅ Apply drillthrough params once, after hydration
  useApplyTxQueryParams<TransactionFilters>(setFilters, setShowFuture, filtersHydrated);

  // Sync filters to localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_FILTER_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem(LOCAL_LISTMODE_KEY, listMode);
  }, [listMode]);

  useEffect(() => {
    if (!filtersHydrated) return;
    if (filters.tentative === true) setListMode('tentative');
  }, [filtersHydrated, filters.tentative]);



  const { transactions, summary, loading } = useLedgerTransactions(
    refreshKey,
    filters,
    showFuture,
    listMode
  );
  const isWhatIfOnly = listMode === 'tentative';

  const cardCashFlow = isWhatIfOnly
    ? summary?.tentative?.cashFlowBalance ?? 0
    : summary?.cashFlowBalance || 0;
  const cardIncome = isWhatIfOnly ? summary?.tentative?.income ?? 0 : summary?.income || 0;
  const cardExpense = isWhatIfOnly ? summary?.tentative?.expense ?? 0 : summary?.expense || 0;
  const cardSavings = isWhatIfOnly
    ? summary?.tentative?.savings?.cumulative ?? 0
    : summary?.savings?.cumulative || 0;
  const cardNetBalance = isWhatIfOnly
    ? summary?.tentative?.netBalance ?? 0
    : summary?.netBalance || 0;

  const handleAdd = () => {
    setEditingTx(null);
    setModalOpen(true);
  };

  const isSeriesReady = (tx: Transaction): boolean => tx.isRecurring === true && Boolean(tx.seriesId);

  const getAnchorDate = (tx: Transaction): string =>
    tx.date && tx.date.length >= 10 ? tx.date.slice(0, 10) : getTodayUTCString();

  const closeScopeDialog = () => {
    setScopeOpen(false);
    setScopeTx(null);
  };

  const getResponseMessage = async (res: Response, fallback: string): Promise<string> => {
    try {
      const data = await res.json();
      return data?.message || fallback;
    } catch {
      return fallback;
    }
  };

  const handleEdit = (tx: Transaction) => {
    if (isSeriesReady(tx)) {
      setScopeTx(tx);
      setScopeMode('edit');
      setScopeOpen(true);
      return;
    }
    setEditingTx(tx);
    setModalOpen(true);
  };

  const handleDelete = (tx: Transaction) => {
    if (isSeriesReady(tx)) {
      setScopeTx(tx);
      setScopeMode('delete');
      setScopeOpen(true);
      return;
    }
    setDeletingTx(tx);
  };

  const handleSeriesDelete = async (tx: Transaction, nextScope: Exclude<RecurringScope, 'instance'>) => {
    if (!tx.seriesId) return;

    const confirmed =
      nextScope === 'future'
        ? window.confirm('Delete this and all future occurrences?')
        : window.confirm('Delete all occurrences in this series?');
    if (!confirmed) return;

    const params = new URLSearchParams({ scope: nextScope });
    if (nextScope === 'future') params.set('anchorDate', getAnchorDate(tx));

    try {
      const res = await authFetch(`/api/ledger/series/${tx.seriesId}?${params.toString()}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (!res.ok) {
        throw new Error(await getResponseMessage(res, 'Failed to delete recurring series'));
      }

      closeScopeDialog();
      setRefreshKey((prev) => prev + 1);
      alert('Recurring series deleted.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete recurring series';
      alert(message);
    }
  };

  const handleScopeSelect = (scope: RecurringScope) => {
    if (!scopeTx) return;

    if (scopeMode === 'edit') {
      if (scope === 'instance') {
        closeScopeDialog();
        setEditingTx(scopeTx);
        setSeriesScope(null);
        setModalOpen(true);
        return;
      }
      closeScopeDialog();
      setSeriesScope(scope);
      setEditingTx(scopeTx);
      setModalOpen(true);
      return;
    }

    if (scope === 'instance') {
      closeScopeDialog();
      setDeletingTx(scopeTx);
      return;
    }
    void handleSeriesDelete(scopeTx, scope);
  };

  const handleView = (tx: Transaction) => {
    setDetailsTx(tx);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setDetailsTx(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTx) return;
    try {
      const res = await authFetch(`/api/ledger/transactions/${deletingTx._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to delete transaction');
      }

      setDeletingTx(null);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const seriesSubmitOverride =
    editingTx?.seriesId && seriesScope
      ? async (payload: Record<string, unknown>) => {
          const params = new URLSearchParams({ scope: seriesScope });
          if (seriesScope === 'future') params.set('anchorDate', getAnchorDate(editingTx));
          const excludedKeys = new Set(['date', 'paymentStatus', 'paidAt']);
          const payloadWithoutDate = Object.fromEntries(
            Object.entries(payload).filter(([key]) => !excludedKeys.has(key))
          );
          const res = await authFetch(`/api/ledger/series/${editingTx.seriesId}?${params.toString()}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth.token}`,
            },
            body: JSON.stringify({ ...payloadWithoutDate, mutationReason: 'series_edit' }),
          });
          if (!res.ok) {
            const message = await getResponseMessage(res, 'Failed to update recurring series');
            alert(message);
            throw new Error(message);
          }
        }
      : undefined;

  // ✅ Clear filters + localStorage
  const handleClearFilters = () => {
    localStorage.removeItem(LOCAL_FILTER_KEY);
    setFilters({
      type: undefined,
      category: '',
      startDate: '',
      endDate: '',
      isRecurring: undefined,
      tags: [],
    });
    setShowFuture(false); // 👈 reset toggle state
  };


  const todayUTC = getTodayUTCString();
  const dateRangeLabel = formatDateRange(
    filters.startDate,
    filters.endDate || todayUTC
  );


  const shouldShowFutureToggle = !(filters.startDate && filters.endDate);

  const fetchTransactions = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleListModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: TransactionListMode | null
  ) => {
    if (!newMode) return;
    setListMode(newMode);
    setFilters((f) => ({
      ...f,
      tentative: newMode === 'tentative' ? true : undefined,
    }));
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (Array.isArray(filters.tags) && filters.tags.length > 0) {
        params.set('tags', filters.tags.join(','));
      }
      if (filters.type === 'income' || filters.type === 'expense') {
        params.set('kind', filters.type);
      }
      if (listMode === 'tentative') {
        params.set('tentative', 'true');
      } else if (listMode === 'committed') {
        params.set('tentative', 'false');
      }
      if (typeof filters.isRecurring === 'boolean') {
        params.set('isRecurring', String(filters.isRecurring));
      }

      const query = params.toString();
      const url = query ? `/api/ledger/export?${query}` : '/api/ledger/export';
      const headers: Record<string, string> = {};
      if (auth?.token) headers.Authorization = `Bearer ${auth.token}`;

      const res = await authFetch(url, { headers });
      if (!res.ok) {
        let message = 'Export failed';
        try {
          const data = await res.json();
          if (data?.message) message = data.message;
        } catch {
          // keep generic message
        }
        alert(message);
        return;
      }

      const blob = await res.blob();
      const filename =
        getFilenameFromDisposition(res.headers.get('Content-Disposition')) ||
        `ledger-export-${getLocalDateStamp()}.csv`;

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };


  return (
    <>
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            sx={{
              p: { xs: 2.5, sm: 3, md: 4 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(255, 255, 255, 0.98) 55%, rgba(46, 125, 50, 0.08) 100%)',
              boxShadow: '0 18px 40px rgba(15, 23, 42, 0.06)',
            }}
          >
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              alignItems={{ xs: 'flex-start', lg: 'center' }}
              justifyContent="space-between"
              spacing={{ xs: 2, md: 3 }}
            >
              <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: 'rgba(25, 118, 210, 0.10)',
                    color: 'primary.main',
                    width: 'fit-content',
                  }}
                >
                  <TransactionsIcon fontSize="small" />
                  <Typography variant="body2" fontWeight={700}>
                    Operating center
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                    letterSpacing: '-0.02em',
                  }}
                >
                  <TransactionsIcon /> Transactions Log
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.15rem' }, lineHeight: 1.5 }}
                >
                  Review all your activity in one place - stay on top of what&apos;s been spent, earned, and scheduled.
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row', lg: 'column' }}
                spacing={1.25}
                sx={{ width: { xs: '100%', lg: 'auto' }, minWidth: { lg: 220 } }}
              >
                <Button
                  variant="outlined"
                  onClick={handleExportCsv}
                  disabled={exporting}
                  fullWidth={isMobile}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/transactions/bulk-upload')}
                  fullWidth={isMobile}
                >
                  Quick Upload
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  startIcon={<AddIcon />}
                  fullWidth={isMobile}
                >
                  Add Transaction
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              <Stack spacing={0.75}>
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                  Filters & actions
                </Typography>
                <Typography variant="h6">
                  <FilterAltIcon /> Filter Transactions{' '}
                  <span style={{ fontSize: '14px' }}>(Filtered results by type, category, date, etc.)</span>
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 920 }}>
                  <strong>How These Filters Works:</strong> Use the filters below to explore your financial activity.
                  Summary cards and the table below update based on your selection. Selecting a date range will hide the show upcoming transactions.
                  Clear filters anytime to reset.
                </Typography>
              </Stack>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2.5,
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'background.default',
                }}
              >
                <Stack spacing={{ xs: 1.5, sm: 2 }}>
                  <ToggleButtonGroup
                    value={listMode}
                    exclusive
                    size="small"
                    onChange={handleListModeChange}
                    aria-label="transactions view mode"
                    data-testid="transactions-list-mode-toggle"
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                    fullWidth={isMobile}
                    sx={{ width: { xs: '100%', sm: 'fit-content' } }}
                  >
                    <ToggleButton value="committed">Committed</ToggleButton>
                    <ToggleButton value="all">Committed + What-Ifs</ToggleButton>
                    <ToggleButton value="tentative">What-Ifs Only</ToggleButton>
                  </ToggleButtonGroup>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ alignItems: { xs: 'stretch', md: 'flex-end' }, flexWrap: 'wrap' }}
                  >
                    <Box>
                      <Typography variant="caption" sx={{ pl: 0.5, fontWeight: 600, lineHeight: 3.1 }}>
                        Date Range
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          label="Start"
                          type="date"
                          size="small"
                          fullWidth={isMobile}
                          InputLabelProps={{ shrink: true }}
                          value={filters.startDate}
                          onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                        />
                        <TextField
                          label="End"
                          type="date"
                          size="small"
                          fullWidth={isMobile}
                          InputLabelProps={{ shrink: true }}
                          value={filters.endDate}
                          onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                        />
                      </Stack>
                    </Box>

                    <FormControl sx={{ minWidth: { xs: '100%', md: 150 } }} size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={filters.type || ''}
                        label="Type"
                        onChange={(e) =>
                          setFilters((f) => ({
                            ...f,
                            type: (['income', 'expense', 'savings'].includes(e.target.value)
                              ? e.target.value
                              : undefined) as TransactionFilters['type'],
                          }))
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="income">Income</MenuItem>
                        <MenuItem value="expense">Expense</MenuItem>
                        <MenuItem value="savings">Transfers & Card Payments</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: { xs: '100%', md: 220 } }} size="small">
                      <InputLabel id="tags-label">Tags</InputLabel>
                      <Select<string[]>
                        labelId="tags-label"
                        multiple
                        value={filters.tags ?? []}
                        onChange={(e: SelectChangeEvent<string[]>) => {
                          const value = e.target.value;
                          const nextTags =
                            typeof value === 'string'
                              ? value.split(',').map((t) => t.trim()).filter(Boolean)
                              : value;

                          setFilters((f) => ({ ...f, tags: nextTags }));
                        }}
                        input={<OutlinedInput label="Tags" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {TAG_OPTIONS.map((tag) => (
                          <MenuItem key={tag} value={tag}>
                            {tag}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.isRecurring === true}
                          onChange={(e) =>
                            setFilters((f) => ({
                              ...f,
                              isRecurring: e.target.checked ? true : undefined,
                            }))
                          }
                        />
                      }
                      label="Recurring Only"
                    />

                    <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' }, width: { xs: '100%', md: 'auto' } }}>
                      <Button variant="outlined" onClick={handleClearFilters} fullWidth={isMobile}>
                        Clear Filters
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              <Stack spacing={0.75}>
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                  Overview
                </Typography>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoOutlineIcon /> Summary Cards
                </Typography>
                <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                  See where you stand - monitor your income, spending, savings, and cash flow in real time.
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(160px, 1fr))',
                    lg: 'repeat(5, minmax(160px, 1fr))',
                  },
                  gap: { xs: 1.25, sm: 2 },
                }}
              >
                <SummaryCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Cash Flow
                      <Tooltip title="Difference between recurring income and recurring expenses.">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                  value={cardCashFlow}
                  tentativeValue={listMode === 'all' ? summary?.tentative?.cashFlowBalance : undefined}
                  dateRange={dateRangeLabel}
                />
                <SummaryCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Total Income
                      <Tooltip title="All earnings recorded during the selected timeframe.">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                  value={cardIncome}
                  tentativeValue={listMode === 'all' ? summary?.tentative?.income : undefined}
                  dateRange={dateRangeLabel}
                />
                <SummaryCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Total Expenses
                      <Tooltip title="All expenses recorded during the selected timeframe.">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                  value={cardExpense}
                  tentativeValue={listMode === 'all' ? summary?.tentative?.expense : undefined}
                  dateRange={dateRangeLabel}
                />

                <SummaryCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Total Savings
                      <Tooltip
                        title={`All-time saved: $${summary?.savings.cumulative?.toFixed(2) || 0}\nIn range: $${summary?.savings.inRange?.toFixed(2) || 0}`}
                      >
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                  value={cardSavings}
                  tentativeValue={listMode === 'all' ? summary?.tentative?.savings?.cumulative : undefined}
                  subtitle={`In range: $${summary?.savings?.inRange?.toFixed(2) || '0.00'}`}
                  dateRange={dateRangeLabel}
                />

                <SummaryCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Cash Balance
                      <Tooltip title="Cash Balance includes checking accounts only; transfers to savings reduce cash but do not change net worth.">
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </Tooltip>
                    </Box>
                  }
                  value={cardNetBalance}
                  tentativeValue={listMode === 'all' ? summary?.tentative?.netBalance : undefined}
                  dateRange={dateRangeLabel}
                />
              </Box>
            </Stack>
          </Paper>

          <Paper
            elevation={1}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 10px 28px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
                    Ledger
                  </Typography>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DifferenceIcon /> Transactions
                  </Typography>
                  <Typography sx={{ wordWrap: 'break-word', color: 'text.secondary', mt: 0.5 }}>
                    Add, edit, or remove past and future transactions to keep your records accurate and up to date.
                  </Typography>
                </Box>

                {shouldShowFutureToggle && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.5,
                      bgcolor: 'background.default',
                    }}
                  >
                    <FormControlLabel
                      control={<Checkbox checked={showFuture} onChange={(e) => setShowFuture(e.target.checked)} />}
                      label="🔮 Show Future"
                    />
                  </Box>
                )}
              </Stack>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <TransactionTable
                  transactions={transactions}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  showFuture={showFuture}
                  onRefresh={fetchTransactions}
                  loading={loading}
                />
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Container>

      {isMobile && <FloatingActionButton onClick={handleAdd} />}

      <TransactionModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingTx(null);
          setSeriesScope(null);
        }}
        onSuccess={() => {
          setModalOpen(false);
          setEditingTx(null);
          setSeriesScope(null);
          setRefreshKey((prev) => prev + 1);
        }}
        transaction={editingTx}
        submitOverride={seriesSubmitOverride}
        disableDateEdit={Boolean(seriesScope)}
        disablePaymentEdit={Boolean(seriesScope)}
      />

      <DeleteConfirmModal
        open={!!deletingTx}
        onClose={() => setDeletingTx(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Transaction?"
        description={
          deletingTx
            ? `Are you sure you want to delete the "${deletingTx.category}" transaction of $${deletingTx.amount.toFixed(2)}?`
            : ''
        }
      />

      <RecurringScopeDialog
        open={scopeOpen}
        mode={scopeMode}
        transaction={scopeTx}
        onClose={closeScopeDialog}
        onSelect={handleScopeSelect}
      />

      <TransactionDetailsDrawer
        open={detailsOpen}
        transaction={detailsTx}
        onClose={handleCloseDetails}
      />

    </>
  );
};

export default TransactionsPage;
